/**
 * The Add people sheet for one builder session's preview (ADR-040 Q12). The
 * preview is the arche `preview_<sessionId>` to the client-entity registry, so
 * a previewed app's `addPeople()` opens the sheet this hook's page draws, and
 * each add is the page's session call against the preview's own tenant.
 *
 * One request at a time: a second while one waits answers SHEET_BUSY. When the
 * page leaves the session (unmount, or another session opened), a sheet still
 * open closes and answers "no one added": its request belongs to the old
 * preview, and must neither wait out the SDK's timeout nor stay open against
 * the next session's tenant.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkspaceAddPeopleResult } from '@fias/client-entities';
import {
  registerAddPeopleSheet,
  type AddPeopleSheetRequest,
} from '@/lib/workspace-add-people-host';

export interface PreviewAddPeopleSheet {
  /** The waiting request, or null when no sheet is open. */
  request: AddPeopleSheetRequest | null;
  /** Close the sheet with the person's answer. */
  resolve: (result: WorkspaceAddPeopleResult) => void;
}

export function usePreviewAddPeopleSheet(sessionId: string | undefined): PreviewAddPeopleSheet {
  const [request, setRequest] = useState<AddPeopleSheetRequest | null>(null);
  const pendingRef = useRef<((result: WorkspaceAddPeopleResult) => void) | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    const unregister = registerAddPeopleSheet(
      `preview_${sessionId}`,
      (next) =>
        new Promise<WorkspaceAddPeopleResult>((resolvePending) => {
          if (pendingRef.current) {
            resolvePending({
              error: { code: 'SHEET_BUSY', message: 'Another request is waiting for the person' },
            });
            return;
          }
          pendingRef.current = resolvePending;
          setRequest(next);
        }),
    );
    return () => {
      unregister();
      pendingRef.current?.({ added: [] });
      pendingRef.current = null;
      setRequest(null);
    };
  }, [sessionId]);

  const resolve = useCallback((result: WorkspaceAddPeopleResult) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setRequest(null);
    pending?.(result);
  }, []);

  return { request, resolve };
}
